import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Settings2, Pencil, Warehouse as WarehouseIcon, History, Layers, Layers3, Package, Tag, FileText, DollarSign, Ruler, Shirt, PanelTop } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DIALOG_TAB_TRIGGER, SectionHeading } from '@/components/common/DialogPrimitives';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { inventoryService, categoriesService } from '@/services/inventory';
import type { InventoryItem, StockMovementType } from '@/types/inventory';
import { AdjustStockDialog, type AdjustStockTarget } from './AdjustStockDialog';
import { ItemImagesTab } from './ItemImagesTab';
import { ItemHeroTab } from './ItemHeroTab';
import { ItemVideoTab } from './ItemVideoTab';
import { ItemBannersTab } from './ItemBannersTab';
import { TestBadge } from '@/components/test-data/TestBadge';
import { ItemIdentifiersSection } from './ItemIdentifiersSection';
import { ItemVariantsSection } from './ItemVariantsSection';
import { ItemMeasurementsTab } from './ItemMeasurementsTab';
import { ItemFabricCareTab } from './ItemFabricCareTab';
import { ItemCatalogTab } from './ItemCatalogTab';
import { ItemPricesSection } from './ItemPricesSection';
import { ItemBatchesTab } from './ItemBatchesTab';
import { ItemBundleComponentsSection } from './ItemBundleComponentsSection';
import { IMAGE_MAX_LABEL } from './imageProfile';

interface Props {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open straight into the Details tab in edit mode (from "Edit catalog"). */
  initialEditCatalog?: boolean;
}

const MOVEMENT_TONE: Record<StockMovementType, string> = {
  INBOUND: 'bg-success/10 text-success',
  OUTBOUND: 'bg-info/10 text-info',
  ADJUSTMENT_INCREMENT: 'bg-success/10 text-success',
  ADJUSTMENT_DECREMENT: 'bg-warning/15 text-[hsl(38_85%_30%)]',
  TRANSFER_IN: 'bg-accent-50 text-accent-700',
  TRANSFER_OUT: 'bg-accent-50 text-accent-700',
};

const MOVEMENT_SIGN: Record<StockMovementType, '+' | '−'> = {
  INBOUND: '+',
  OUTBOUND: '−',
  ADJUSTMENT_INCREMENT: '+',
  ADJUSTMENT_DECREMENT: '−',
  TRANSFER_IN: '+',
  TRANSFER_OUT: '−',
};

/**
 * A readable variant label for grids/dialogs. Variant names are stored as
 * "<parent> — <attrs>" (e.g. "Washed Cotton Trousers — STRAIGHT / 32"); we
 * strip the repeated parent prefix to surface just the distinguishing
 * size/attributes ("STRAIGHT / 32"). Falls back to the full name, then SKU.
 */
function variantShortLabel(
  name: string | null | undefined,
  sku: string,
  parentName: string | null | undefined,
): string {
  const full = (name ?? '').trim() || sku;
  if (parentName && full.toLowerCase().startsWith(parentName.trim().toLowerCase())) {
    const suffix = full.slice(parentName.trim().length).replace(/^\s*[—–-]\s*/, '').trim();
    if (suffix) return suffix;
  }
  return full;
}

export function InventoryDetailSheet({
  item,
  open,
  onOpenChange,
  initialEditCatalog,
}: Props) {
  // The specific stock row (variant × warehouse) being adjusted. Tracking
  // the ROW — not just a warehouse — means a parent-with-variants adjusts the
  // chosen VARIANT, never the parent (which can't hold stock).
  const [adjustRowId, setAdjustRowId] = useState<string | null>(null);
  // Controlled tab + in-place catalog edit. Reset each time the sheet
  // opens (or switches item) — "Edit catalog" lands straight in edit.
  const [activeTab, setActiveTab] = useState('stock');
  const [catalogEditing, setCatalogEditing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialEditCatalog ? 'details' : 'stock');
    setCatalogEditing(!!initialEditCatalog);
  }, [open, item?.id, initialEditCatalog]);

  // Phase C — for parent-with-variants items, the parent itself has
  // no stock_levels (variants are where stock actually lives). The
  // BE's `parentItemId` filter returns stock rows for all variants
  // so the Stock tab can show a variant × warehouse pivot. Same
  // pattern for movements.
  const isParentWithVariants =
    !!item && (item.hasVariants || (item.variantCount ?? 0) > 0);
  const stockQuery = useQuery({
    queryKey: [
      'inventory',
      'stock',
      isParentWithVariants ? { parent: item?.id } : { item: item?.id },
    ],
    queryFn: () =>
      inventoryService.listStock(
        isParentWithVariants
          ? { parentItemId: item!.id, pageSize: 500 }
          : { itemId: item!.id, pageSize: 100 },
      ),
    enabled: !!item && open,
  });

  const movementsQuery = useQuery({
    queryKey: [
      'inventory',
      'movements',
      isParentWithVariants ? { parent: item?.id } : { item: item?.id },
    ],
    queryFn: () =>
      inventoryService.listMovements(
        isParentWithVariants
          ? { parentItemId: item!.id, pageSize: 60 }
          : { itemId: item!.id, pageSize: 30 },
      ),
    enabled: !!item && open,
  });

  const categoriesQuery = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => categoriesService.list(),
    staleTime: 60_000,
  });

  const stockRows = stockQuery.data?.rows ?? [];
  const movements = movementsQuery.data?.rows ?? [];
  const category = useMemo(
    () => categoriesQuery.data?.find((c) => c.id === item?.categoryId),
    [categoriesQuery.data, item?.categoryId],
  );

  const totals = useMemo(() => {
    const onHand = stockRows.reduce((s, r) => s + Number(r.quantityOnHand), 0);
    const reserved = stockRows.reduce((s, r) => s + Number(r.quantityReserved), 0);
    return { onHand, reserved, available: Math.max(0, onHand - reserved) };
  }, [stockRows]);

  // Build the AdjustStockDialog target from the selected stock ROW — its
  // item is the actual stockable SKU (a variant for parent items, or the
  // item itself for standalone), so the adjustment always lands on a row
  // that can hold stock.
  const adjustRow = adjustRowId ? stockRows.find((r) => r.id === adjustRowId) ?? null : null;
  const adjustTarget: AdjustStockTarget | null =
    item && adjustRow
      ? {
          ...item,
          id: adjustRow.item.id,
          sku: adjustRow.item.sku,
          name: adjustRow.item.name ?? item.name,
          warehouseId: adjustRow.warehouseId,
          warehouseName: adjustRow.warehouse.name ?? null,
          quantityOnHand: Number(adjustRow.quantityOnHand),
        }
      : null;

  // Header quick-adjust — only for standalone items (one stockable SKU).
  // Parent-with-variants is ambiguous (which variant?), so the header button
  // is hidden and the operator adjusts each variant row in the Stock tab.
  const headerAdjustRow = isParentWithVariants
    ? null
    : stockRows.find((r) => Number(r.quantityOnHand) > 0) ?? stockRows[0] ?? null;

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[88vh] w-full max-w-6xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-7 pt-6 pb-5">
            <DialogTitle className="sr-only">{item?.sku ?? 'Inventory item'}</DialogTitle>
            {item && (
              <div className="flex items-start gap-4">
                {/* Hero — primary image when available, branded fallback otherwise */}
                {item.primaryImageUrl ? (
                  <img
                    src={item.primaryImageUrl}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-lg border border-border/70 object-cover"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
                  >
                    <Boxes className="h-6 w-6" />
                  </span>
                )}

                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                    <h2 className="text-[18px] font-semibold leading-tight tracking-tight text-foreground">
                      {item.name}
                    </h2>
                    <span className="font-mono text-[11.5px] text-muted-foreground">
                      {item.sku}
                    </span>
                    {item.isTestData && <TestBadge />}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {item.status !== 'ACTIVE' && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        {item.status.toLowerCase()}
                      </Badge>
                    )}
                    <Badge variant="muted" className="text-[10px]">
                      {item.itemType === 'SERVICE' ? 'Service' : 'Goods'}
                    </Badge>
                    {category && (
                      <Badge variant="muted" className="text-[10px]">
                        <span className="font-mono">{category.code}</span>
                        <span className="ml-1">{category.name}</span>
                      </Badge>
                    )}
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {item.unitOfMeasure}
                    </Badge>
                    {item.hsnCode && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        HSN {item.hsnCode}
                      </Badge>
                    )}
                    {item.tracksBatches && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        batched
                      </Badge>
                    )}
                    {item.isBundle && (
                      <Badge variant="muted" className="font-mono text-[10px]">
                        bundle
                      </Badge>
                    )}
                    {item.brand && (
                      <span className="text-[11px] text-muted-foreground">
                        · {item.brand}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {headerAdjustRow && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAdjustRowId(headerAdjustRow.id)}
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                          Adjust
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Adjust stock at {headerAdjustRow.warehouse.name ?? 'a warehouse'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setActiveTab('details');
                      setCatalogEditing(true);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />

          {/* Stats strip — single row, subtle dividers, no boxed cards. */}
          <div className="flex items-stretch divide-x divide-border/60 border-b border-border/60 bg-muted/20 px-7">
            <Stat label="On hand" value={fmt.number(totals.onHand)} accent />
            <Stat label="Available" value={fmt.number(totals.available)} />
            <Stat label="Reserved" value={fmt.number(totals.reserved)} muted />
            <Stat
              label="Reorder at"
              value={item?.reorderPoint != null ? fmt.number(item.reorderPoint) : '—'}
              muted
            />
            {item?.sellingPrice != null && (
              <Stat label="Sell price" value={fmt.inr(item.sellingPrice)} />
            )}
            {item?.costPrice != null && (
              <Stat label="Cost" value={fmt.inr(item.costPrice)} muted />
            )}
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="overflow-x-auto border-b border-border/60 bg-background px-7">
              <TabsList className="h-10 w-max flex-nowrap gap-2 bg-transparent p-0">
                <TabsTrigger value="stock" className={DIALOG_TAB_TRIGGER}>
                  <WarehouseIcon className="h-3.5 w-3.5" />
                  Stock
                </TabsTrigger>
                {item?.tracksBatches && (
                  <TabsTrigger value="batches" className={DIALOG_TAB_TRIGGER}>
                    <Layers3 className="h-3.5 w-3.5" />
                    Batches
                  </TabsTrigger>
                )}
                {item?.isBundle && (
                  <TabsTrigger value="components" className={DIALOG_TAB_TRIGGER}>
                    <Package className="h-3.5 w-3.5" />
                    Components
                  </TabsTrigger>
                )}
                {item?.itemType !== 'SERVICE' && !item?.isBundle && (
                  <TabsTrigger value="variants" className={DIALOG_TAB_TRIGGER}>
                    <Layers className="h-3.5 w-3.5" />
                    Variants
                  </TabsTrigger>
                )}
                {isParentWithVariants && (
                  <TabsTrigger value="measurements" className={DIALOG_TAB_TRIGGER}>
                    <Ruler className="h-3.5 w-3.5" />
                    Measurements
                  </TabsTrigger>
                )}
                {item?.itemType !== 'SERVICE' && !item?.isBundle && (
                  <TabsTrigger value="fabric-care" className={DIALOG_TAB_TRIGGER}>
                    <Shirt className="h-3.5 w-3.5" />
                    Fabric &amp; Care
                  </TabsTrigger>
                )}
                <TabsTrigger value="prices" className={DIALOG_TAB_TRIGGER}>
                  <DollarSign className="h-3.5 w-3.5" />
                  Sale Prices
                </TabsTrigger>
                <TabsTrigger value="activity" className={DIALOG_TAB_TRIGGER}>
                  <History className="h-3.5 w-3.5" />
                  Activity
                </TabsTrigger>
                <TabsTrigger value="media" className={DIALOG_TAB_TRIGGER}>
                  <Tag className="h-3.5 w-3.5" />
                  Media &amp; IDs
                </TabsTrigger>
                <TabsTrigger value="hero" className={DIALOG_TAB_TRIGGER}>
                  <PanelTop className="h-3.5 w-3.5" />
                  Storefront images
                </TabsTrigger>
                <TabsTrigger value="details" className={DIALOG_TAB_TRIGGER}>
                  <FileText className="h-3.5 w-3.5" />
                  Details
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-7 py-5">
                <TabsContent value="stock" className="m-0 space-y-4">
                  {isParentWithVariants && (
                    <div className="rounded-md border border-info/30 bg-info/[0.04] px-3 py-2 text-[11.5px] text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        Variant-tracked item.
                      </span>{' '}
                      Stock lives on each variant, not the parent — use the{' '}
                      <span className="font-medium text-foreground">Adjust</span> button on a row
                      to correct that variant&rsquo;s stock. Numbers aggregate across every
                      variant; per-variant breakdowns also live on the Variants tab.
                    </div>
                  )}
                  <SectionHeading
                    title={isParentWithVariants ? 'Per-variant × warehouse stock' : 'Per-warehouse stock'}
                    hint={
                      stockRows.length === 0
                        ? 'No stock yet'
                        : isParentWithVariants
                          ? `${stockRows.length} variant-warehouse cell${stockRows.length === 1 ? '' : 's'}`
                          : `${stockRows.length} warehouse${stockRows.length === 1 ? '' : 's'}`
                    }
                  />
                  {stockRows.length > 0 && (
                    <div className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-[11.5px] text-muted-foreground">
                      <Settings2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span>
                        To correct a row — cycle count, damaged unit, found stock — click{' '}
                        <span className="font-medium text-foreground">Adjust</span> on that row.
                        Every change is logged to the audit trail with your reason.
                      </span>
                    </div>
                  )}
                  {stockQuery.isLoading ? (
                    <div className="py-12 text-center text-[12.5px] text-muted-foreground">
                      Loading…
                    </div>
                  ) : stockRows.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center">
                      <WarehouseIcon className="mx-auto h-5 w-5 text-muted-foreground/50" />
                      <div className="mt-2 text-[12.5px] text-muted-foreground">
                        {isParentWithVariants
                          ? 'No variant has stock yet.'
                          : 'No stock recorded for this SKU yet.'}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground/70">
                        Add stock via inward receipt or stock adjustment.
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border border-border/70">
                      <div className="grid grid-cols-12 gap-3 border-b border-border/60 bg-muted/40 px-4 py-2 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                        <div className="col-span-4">
                          {isParentWithVariants ? 'Variant @ Warehouse' : 'Warehouse'}
                        </div>
                        <div className="col-span-2 text-right">On hand</div>
                        <div className="col-span-2 text-right">Reserved</div>
                        <div className="col-span-2 text-right">Available</div>
                        <div className="col-span-2 text-right">Adjust</div>
                      </div>
                      {stockRows.map((s) => {
                        const onHand = Number(s.quantityOnHand);
                        const reserved = Number(s.quantityReserved);
                        const available = Math.max(0, onHand - reserved);
                        // Variant label — show the human name/size (e.g.
                        // "STRAIGHT / 32"), stripping the repeated parent-name
                        // prefix; the cryptic SKU drops to the secondary line.
                        const variantLabel = variantShortLabel(s.item.name, s.item.sku, item?.name);
                        return (
                          <div
                            key={s.id}
                            className="group grid grid-cols-12 items-center gap-3 border-b border-border/60 px-4 py-2.5 last:border-0 hover:bg-muted/20"
                          >
                            <div className="col-span-4 min-w-0">
                              <div className="flex flex-wrap items-baseline gap-1.5">
                                <span className="truncate text-[13px] font-medium text-foreground">
                                  {isParentWithVariants ? variantLabel : s.warehouse.name}
                                </span>
                                {isParentWithVariants && (
                                  <span className="text-[11px] text-muted-foreground">
                                    @ {s.warehouse.name}
                                  </span>
                                )}
                              </div>
                              <span className="truncate font-mono text-[10.5px] text-muted-foreground">
                                {isParentWithVariants
                                  ? `${s.item.sku} · ${s.warehouse.code}`
                                  : s.warehouse.code}
                              </span>
                            </div>
                            <div className="col-span-2 text-right font-mono text-[13px] tabular-nums text-foreground">
                              {fmt.number(onHand)}
                            </div>
                            <div className="col-span-2 text-right font-mono text-[12.5px] tabular-nums text-muted-foreground">
                              {fmt.number(reserved)}
                            </div>
                            <div className="col-span-2 text-right font-mono text-[12.5px] tabular-nums text-foreground">
                              {fmt.number(available)}
                            </div>
                            <div className="col-span-2 flex justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 px-2 text-[11px]"
                                onClick={() => setAdjustRowId(s.id)}
                                title={
                                  isParentWithVariants
                                    ? `Adjust ${s.item.sku} stock`
                                    : 'Adjust stock'
                                }
                              >
                                <Settings2 className="h-3.5 w-3.5" />
                                Adjust
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {/* Aggregate totals row */}
                      {stockRows.length > 1 && (
                        <div className="grid grid-cols-12 gap-3 border-t border-border/60 bg-muted/30 px-4 py-2 text-[11.5px] font-semibold text-muted-foreground">
                          <div className="col-span-4">Total</div>
                          <div className="col-span-2 text-right font-mono tabular-nums text-foreground">
                            {fmt.number(totals.onHand)}
                          </div>
                          <div className="col-span-2 text-right font-mono tabular-nums">
                            {fmt.number(totals.reserved)}
                          </div>
                          <div className="col-span-2 text-right font-mono tabular-nums text-foreground">
                            {fmt.number(totals.available)}
                          </div>
                          <div className="col-span-2" />
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="m-0 space-y-4">
                  <SectionHeading
                    title="Recent stock movements"
                    hint={movements.length > 0 ? `${movements.length} most recent` : undefined}
                  />
                  {movementsQuery.isLoading ? (
                    <div className="py-12 text-center text-[12.5px] text-muted-foreground">
                      Loading…
                    </div>
                  ) : movements.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border/70 px-4 py-10 text-center">
                      <History className="mx-auto h-5 w-5 text-muted-foreground/50" />
                      <div className="mt-2 text-[12.5px] text-muted-foreground">
                        No stock movements recorded for this SKU yet.
                      </div>
                    </div>
                  ) : (
                    <ol className="space-y-1">
                      {movements.map((m, idx) => (
                        <li key={m.id} className="relative pl-8 pb-4 last:pb-0">
                          <span
                            className={cn(
                              'absolute left-0 top-1 grid h-5 w-5 place-items-center rounded-full text-[11px] font-bold ring-4 ring-background',
                              MOVEMENT_TONE[m.movementType],
                            )}
                          >
                            {MOVEMENT_SIGN[m.movementType]}
                          </span>
                          {idx < movements.length - 1 && (
                            <span className="absolute left-[9px] top-6 h-full w-px bg-border" />
                          )}
                          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                            <span className="font-mono text-[13.5px] font-medium tabular-nums text-foreground">
                              {MOVEMENT_SIGN[m.movementType]}
                              {fmt.number(Number(m.quantity))}
                            </span>
                            <span className="text-[12.5px] text-foreground">
                              {humanizeMovementType(m.movementType)}
                            </span>
                            {/* Phase C — for parent-with-variants Activity tab,
                                label each movement with its variant SKU so
                                operators can see "INBOUND · TSHIRT-RED-M · 12". */}
                            {isParentWithVariants && m.item && (
                              <span className="font-mono text-[11.5px] text-muted-foreground">
                                · {m.item.sku}
                              </span>
                            )}
                            {m.warehouse && (
                              <span className="text-[11.5px] text-muted-foreground">
                                · {m.warehouse.name}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground">
                            {fmt.dateTime(m.createdAt)}
                            {isParentWithVariants && m.item?.variantAttributes && (
                              <>
                                {' · '}
                                {Object.entries(m.item.variantAttributes)
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(' · ')}
                              </>
                            )}
                            {m.reason ? <> · {m.reason}</> : null}
                            {m.referenceId ? <> · ref {m.referenceType}/{m.referenceId.slice(0, 8)}</> : null}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </TabsContent>

                <TabsContent value="media" className="m-0 space-y-6">
                  <section className="space-y-3">
                    <SectionHeading title="Images" hint={`Hero + gallery (up to 15 · ${IMAGE_MAX_LABEL} each)`} />
                    <ItemImagesTab itemId={item?.id ?? null} />
                  </section>
                  <Separator />
                  {/* Product video — its own highlighted slot, deliberately
                      apart from the image grid: one clip per item, surfaced
                      by the portal as a distinct media type. */}
                  <section className="space-y-3">
                    <SectionHeading
                      title="Product video"
                      hint="One clip · MP4 · up to 60s · highlighted on the storefront product page"
                    />
                    <div className="rounded-lg border border-primary/25 bg-primary/[0.03] p-4">
                      <ItemVideoTab itemId={item?.id ?? null} />
                    </div>
                  </section>
                  <Separator />
                  <section className="space-y-3">
                    <SectionHeading title="Identifiers" hint="UPC, EAN, model #, mfr part — searchable at scan time" />
                    <ItemIdentifiersSection itemId={item?.id ?? null} />
                  </section>
                </TabsContent>

                <TabsContent value="hero" className="m-0 space-y-5">
                  <SectionHeading title="Hero Banner" hint="Wide storefront product-page header — exposed to portal as heroImageUrl" />
                  <ItemHeroTab itemId={item?.id ?? null} />
                  <div className="border-t border-border/60 pt-5">
                    <SectionHeading
                      title="Portal banners"
                      hint="Add as many as you need per placement; the storefront renders Feature, Card & Mobile"
                    />
                    <div className="mt-3">
                      <ItemBannersTab itemId={item?.id ?? null} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="variants" className="m-0">
                  <ItemVariantsSection item={item} />
                </TabsContent>

                {isParentWithVariants && item && (
                  <TabsContent value="measurements" className="m-0">
                    <ItemMeasurementsTab item={item} />
                  </TabsContent>
                )}

                {item && item.itemType !== 'SERVICE' && !item.isBundle && (
                  <TabsContent value="fabric-care" className="m-0">
                    <ItemFabricCareTab item={item} />
                  </TabsContent>
                )}

                <TabsContent value="prices" className="m-0 space-y-4">
                  <SectionHeading
                    title="Sale prices"
                    hint="Base + additional currencies"
                  />
                  <ItemPricesSection item={item} />
                </TabsContent>

                <TabsContent value="batches" className="m-0">
                  <ItemBatchesTab item={item} />
                </TabsContent>

                <TabsContent value="components" className="m-0">
                  <ItemBundleComponentsSection item={item} />
                </TabsContent>

                <TabsContent value="details" className="m-0">
                  {item && (
                    <ItemCatalogTab
                      item={item}
                      editing={catalogEditing}
                      onEditingChange={setCatalogEditing}
                    />
                  )}
                  {item && (
                    <div className="mt-6 border-t border-border/60 pt-5">
                      <SectionHeading
                        title="Fulfilment"
                        hint="Drives shipping weight + the dimensions sent to carriers — edit in Edit catalog → Fulfilment"
                      />
                      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2.5 text-[12.5px] sm:grid-cols-3">
                        {[
                          ['Dimensions',
                            item.dimensionLength && item.dimensionWidth && item.dimensionHeight
                              ? `${item.dimensionLength}×${item.dimensionWidth}×${item.dimensionHeight} ${item.dimensionUnit}`
                              : '—'],
                          ['Weight', item.weight != null ? `${item.weight} ${item.weightUnit}` : '—'],
                          ['Packaged weight', item.packagedWeight != null ? `${item.packagedWeight} ${item.weightUnit}` : '—'],
                          ['Units / carton', item.unitsPerCarton ?? '—'],
                          ['Fragile', item.fragile ? 'Yes' : 'No'],
                          ['Hazmat', item.hazmat ? 'Yes' : 'No'],
                        ].map(([k, v]) => (
                          <div key={String(k)} className="flex flex-col">
                            <dt className="text-[11px] text-muted-foreground">{k}</dt>
                            <dd className="text-foreground">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      <AdjustStockDialog
        open={!!adjustRowId}
        onOpenChange={(o) => !o && setAdjustRowId(null)}
        item={adjustTarget}
      />
    </TooltipProvider>
  );
}

function Stat({
  label,
  value,
  accent,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex-1 min-w-[110px] px-4 py-3.5 first:pl-0 last:pr-0">
      <div className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1.5 font-mono text-[19px] leading-none tabular-nums',
          accent ? 'font-semibold text-foreground' : muted ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function humanizeMovementType(t: StockMovementType): string {
  switch (t) {
    case 'INBOUND': return 'Inward receipt';
    case 'OUTBOUND': return 'Outbound shipment';
    case 'ADJUSTMENT_INCREMENT': return 'Stock adjustment (added)';
    case 'ADJUSTMENT_DECREMENT': return 'Stock adjustment (removed)';
    case 'TRANSFER_IN': return 'Transfer in';
    case 'TRANSFER_OUT': return 'Transfer out';
  }
}
